import React, { useState, useEffect } from 'react'
import { 
  Users,
  ShoppingCart,
  Package,
  DollarSign,
  TrendingUp,
  CheckCircle,
  XCircle,
  Clock,
  Filter,
  Download,
  Star
} from 'lucide-react'
import Breadcrumb from '../../components/Common/Breadcrumb'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import supplierReportsService from '../../services/supplierReportsService'
import DataTable from '../../components/DataTable/DataTable'

const SupplierReports = () => {
  const navigate = useNavigate()
  const [reportsData, setReportsData] = useState(null)
  const [filterOptions, setFilterOptions] = useState(null)
  const [loading, setLoading] = useState(true)
  const [filters, setFilters] = useState({
    startDate: '',
    endDate: '',
    supplierId: ''
  })

  useEffect(() => {
    loadFilterOptions()
    loadReportsData()
  }, [])

  useEffect(() => {
    loadReportsData()
  }, [filters])

  const loadFilterOptions = async () => {
    try {
      const response = await supplierReportsService.getSupplierFilterOptions()
      if (response.success) {
        setFilterOptions(response.data)
        // Set default date range (last 12 months)
        const endDate = new Date()
        const startDate = new Date()
        startDate.setMonth(startDate.getMonth() - 12)
        setFilters(prev => ({
          ...prev,
          startDate: startDate.toISOString().split('T')[0],
          endDate: endDate.toISOString().split('T')[0]
        }))
      }
    } catch (error) {
      console.error('Error loading filter options:', error)
      if (!error._errorHandled) {
        toast.error('Failed to load filter options')
      }
    }
  }

  const loadReportsData = async () => {
    try {
      setLoading(true)
      const response = await supplierReportsService.getSupplierReports(filters)
      if (response.success) {
        setReportsData(response.data)
      } else {
        toast.error(response.message || 'Failed to load supplier reports')
      }
    } catch (error) {
      console.error('Error loading supplier reports:', error)
      if (!error._errorHandled) {
        toast.error('Failed to load supplier reports')
      }
    } finally {
      setLoading(false)
    }
  }

  const handleFilterChange = (e) => {
    const { name, value } = e.target
    setFilters(prev => ({
      ...prev,
      [name]: value
    }))
  }

  const handleResetFilters = () => {
    const endDate = new Date()
    const startDate = new Date()
    startDate.setMonth(startDate.getMonth() - 12)
    setFilters({
      startDate: startDate.toISOString().split('T')[0],
      endDate: endDate.toISOString().split('T')[0],
      supplierId: ''
    })
  }

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2
    }).format(amount || 0)
  }

  const formatNumber = (num) => {
    return new Intl.NumberFormat('en-IN').format(num || 0)
  }

  const formatPercentage = (num) => {
    return `${Math.round(num * 100) / 100}%`
  }

  // Supplier Performance Table Columns
  const supplierPerformanceColumns = [
    {
      accessorKey: 'supplierName',
      header: 'Supplier',
      cell: ({ getValue, row }) => (
        <div style={{ textAlign: 'left' }}>
          <div style={{ fontWeight: '500' }}>{getValue() || '-'}</div>
          {row.original.supplierCode && (
            <div style={{ fontSize: '12px', color: 'var(--color-text-secondary, #5B6478)' }}>{row.original.supplierCode}</div>
          )}
        </div>
      ),
    },
    {
      accessorKey: 'totalOrders',
      header: 'Total Orders',
      cell: ({ getValue }) => (
        <div style={{ textAlign: 'center' }}>
          {formatNumber(getValue())}
        </div>
      ),
    },
    {
      accessorKey: 'totalAmount',
      header: 'Total Amount',
      cell: ({ getValue }) => (
        <div style={{ textAlign: 'right', fontWeight: '600' }}>
          {formatCurrency(getValue())}
        </div>
      ),
    },
    {
      accessorKey: 'averageOrderValue',
      header: 'Avg. Order Value',
      cell: ({ getValue }) => (
        <div style={{ textAlign: 'right' }}>
          {formatCurrency(getValue())}
        </div>
      ),
    },
    {
      accessorKey: 'performanceMetrics.qualityAcceptanceRate',
      header: 'Quality Acceptance',
      cell: ({ getValue }) => {
        const rate = getValue() || 0
        return (
          <div style={{ textAlign: 'center' }}>
            <span style={{ 
              color: rate >= 95 ? '#28a745' : rate >= 80 ? '#ffc107' : '#dc3545',
              fontWeight: '600'
            }}>
              {formatPercentage(rate)}
            </span>
          </div>
        )
      },
    },
    {
      accessorKey: 'performanceMetrics.onTimeDeliveryRate',
      header: 'On-Time Delivery',
      cell: ({ getValue }) => {
        const rate = getValue() || 0
        return (
          <div style={{ textAlign: 'center' }}>
            <span style={{ 
              color: rate >= 90 ? '#28a745' : rate >= 70 ? '#ffc107' : '#dc3545',
              fontWeight: '600'
            }}>
              {formatPercentage(rate)}
            </span>
          </div>
        )
      },
    },
    {
      accessorKey: 'performanceMetrics.averageDeliveryDays',
      header: 'Avg. Delivery Days',
      cell: ({ getValue }) => (
        <div style={{ textAlign: 'center' }}>
          {formatNumber(getValue())} days
        </div>
      ),
    },
    {
      accessorKey: 'invoiceData.totalOutstanding',
      header: 'Outstanding',
      cell: ({ getValue }) => {
        const amount = getValue() || 0
        return (
          <div style={{ textAlign: 'right', fontWeight: '600', color: amount > 0 ? '#dc3545' : '#28a745' }}>
            {formatCurrency(amount)}
          </div>
        )
      },
    },
  ]

  // Top Suppliers Table Columns (simplified)
  const topSuppliersColumns = [
    {
      accessorKey: 'supplierName',
      header: 'Supplier',
      cell: ({ getValue, row }) => (
        <div style={{ textAlign: 'left' }}>
          <div style={{ fontWeight: '500' }}>{getValue() || '-'}</div>
          {row.original.supplierCode && (
            <div style={{ fontSize: '12px', color: 'var(--color-text-secondary, #5B6478)' }}>{row.original.supplierCode}</div>
          )}
        </div>
      ),
    },
    {
      accessorKey: 'totalOrders',
      header: 'Orders',
      cell: ({ getValue }) => (
        <div style={{ textAlign: 'center' }}>
          {formatNumber(getValue())}
        </div>
      ),
    },
    {
      accessorKey: 'totalAmount',
      header: 'Total Amount',
      cell: ({ getValue }) => (
        <div style={{ textAlign: 'right', fontWeight: '600' }}>
          {formatCurrency(getValue())}
        </div>
      ),
    },
    {
      accessorKey: 'performanceMetrics.qualityAcceptanceRate',
      header: 'Quality Rate',
      cell: ({ getValue }) => {
        const rate = getValue() || 0
        return (
          <div style={{ textAlign: 'center' }}>
            <span style={{ 
              color: rate >= 95 ? '#28a745' : rate >= 80 ? '#ffc107' : '#dc3545',
              fontWeight: '600'
            }}>
              {formatPercentage(rate)}
            </span>
          </div>
        )
      },
    },
  ]

  // Calculate summary statistics
  const summaryStats = reportsData ? {
    totalSuppliers: reportsData.supplierPerformance?.length || 0,
    totalOrders: reportsData.supplierPerformance?.reduce((sum, s) => sum + (s.totalOrders || 0), 0) || 0,
    totalAmount: reportsData.supplierPerformance?.reduce((sum, s) => sum + (s.totalAmount || 0), 0) || 0,
    averageQualityRate: reportsData.supplierPerformance?.length > 0
      ? reportsData.supplierPerformance.reduce((sum, s) => sum + (s.performanceMetrics?.qualityAcceptanceRate || 0), 0) / reportsData.supplierPerformance.length
      : 0
  } : null

  const breadcrumbItems = [
    { label: 'Procurement', path: '/procurement' },
    { label: 'Reports', path: '/procurement?tab=reports' },
    { label: 'Supplier Reports', isCurrent: true }
  ]

  const handleExport = () => {
    toast.info('Export functionality coming soon!')
  }

  return (
    <div className="sales-dashboard">
      <Breadcrumb items={breadcrumbItems} />

      <div className="page-header">
        <div className="page-title">
          <h1>Supplier Reports</h1>
          <p>Supplier performance and evaluation</p>
        </div>
        <div className="page-actions">
          <button type="button" className="btn btn-outline-secondary btn-sm" onClick={handleExport}>
            <Download size={16} />
            Export
          </button>
        </div>
      </div>

      {/* Filters Section */}
      <div style={{
        background: '#fff',
        border: '1px solid #e9ecef',
        borderRadius: '8px',
        padding: '20px',
        marginBottom: '24px'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
          <Filter size={18} />
          <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '600' }}>Filters</h3>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px' }}>
          <div>
            <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', color: 'var(--color-text-secondary, #5B6478)' }}>
              Start Date
            </label>
            <input
              type="date"
              name="startDate"
              value={filters.startDate}
              onChange={handleFilterChange}
              style={{
                width: '100%',
                padding: '8px 12px',
                border: '1px solid #e9ecef',
                borderRadius: '6px',
                fontSize: '14px',
                fontFamily: 'Inter, sans-serif'
              }}
            />
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', color: 'var(--color-text-secondary, #5B6478)' }}>
              End Date
            </label>
            <input
              type="date"
              name="endDate"
              value={filters.endDate}
              onChange={handleFilterChange}
              style={{
                width: '100%',
                padding: '8px 12px',
                border: '1px solid #e9ecef',
                borderRadius: '6px',
                fontSize: '14px',
                fontFamily: 'Inter, sans-serif'
              }}
            />
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', color: 'var(--color-text-secondary, #5B6478)' }}>
              Supplier
            </label>
            <select
              name="supplierId"
              value={filters.supplierId}
              onChange={handleFilterChange}
              style={{
                width: '100%',
                padding: '8px 12px',
                border: '1px solid #e9ecef',
                borderRadius: '6px',
                fontSize: '14px',
                fontFamily: 'Inter, sans-serif',
                background: '#fff'
              }}
            >
              <option value="">All Suppliers</option>
              {filterOptions?.suppliers?.map(supplier => (
                <option key={supplier._id} value={supplier._id}>
                  {supplier.supplierName} {supplier.supplierCode ? `(${supplier.supplierCode})` : ''}
                </option>
              ))}
            </select>
          </div>
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: '8px' }}>
            <button
              onClick={handleResetFilters}
              style={{
                padding: '8px 16px',
                border: '1px solid #e9ecef',
                borderRadius: '6px',
                background: '#fff',
                cursor: 'pointer',
                fontSize: '14px',
                fontFamily: 'Inter, sans-serif',
                color: '#495057'
              }}
            >
              Reset
            </button>
          </div>
        </div>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <div className="spinner-border text-primary" role="status">
            <span className="sr-only">Loading...</span>
          </div>
          <p style={{ marginTop: '16px', color: 'var(--color-text-secondary, #5B6478)' }}>Loading supplier reports...</p>
        </div>
      ) : reportsData ? (
        <>
          {/* Summary Cards */}
          {summaryStats && (
            <div className="dashboard-metrics-grid">
              <div className="metric-card deals">
                <div className="metric-header">
                  <div className="metric-icon">
                    <Users size={16} />
                  </div>
                  <div className="metric-trend positive">
                    <span>Suppliers</span>
                  </div>
                </div>
                <div className="metric-value">{formatNumber(summaryStats.totalSuppliers)}</div>
                <div className="metric-title">Total Suppliers</div>
                <div className="metric-subtitle">In report scope</div>
              </div>

              <div className="metric-card revenue">
                <div className="metric-header">
                  <div className="metric-icon">
                    <ShoppingCart size={16} />
                  </div>
                  <div className="metric-trend positive">
                    <span>Orders</span>
                  </div>
                </div>
                <div className="metric-value">{formatNumber(summaryStats.totalOrders)}</div>
                <div className="metric-title">Total Orders</div>
                <div className="metric-subtitle">Purchase volume</div>
              </div>

              <div className="metric-card burn">
                <div className="metric-header">
                  <div className="metric-icon">
                    <DollarSign size={16} />
                  </div>
                  <div className="metric-trend positive">
                    <span>Spend</span>
                  </div>
                </div>
                <div className="metric-value">{formatCurrency(summaryStats.totalAmount)}</div>
                <div className="metric-title">Total Amount</div>
                <div className="metric-subtitle">Combined spend</div>
              </div>

              <div className="metric-card conversion">
                <div className="metric-header">
                  <div className="metric-icon">
                    <Star size={16} />
                  </div>
                  <div className="metric-trend neutral">
                    <span>Quality</span>
                  </div>
                </div>
                <div className="metric-value">{formatPercentage(summaryStats.averageQualityRate)}</div>
                <div className="metric-title">Avg. Quality Rate</div>
                <div className="metric-subtitle">Acceptance rate</div>
              </div>
            </div>
          )}

          {/* Top Suppliers */}
          <div style={{
            background: '#fff',
            border: '1px solid #e9ecef',
            borderRadius: '8px',
            padding: '24px',
            marginBottom: '24px'
          }}>
            <h3 style={{ margin: '0 0 16px 0', fontSize: '18px', fontWeight: '600' }}>Top 10 Suppliers</h3>
            <div className="data-table-section">
              <DataTable
                data={reportsData.topSuppliers || []}
                columns={topSuppliersColumns}
                loading={false}
                enableSelection={false}
                noCard={true}
                className="supplier-reports-top-table"
                pageSize={10}
              />
            </div>
          </div>

          {/* Supplier Performance */}
          <div style={{
            background: '#fff',
            border: '1px solid #e9ecef',
            borderRadius: '8px',
            padding: '24px',
            marginBottom: '24px'
          }}>
            <h3 style={{ margin: '0 0 16px 0', fontSize: '18px', fontWeight: '600' }}>Supplier Performance</h3>
            <div className="data-table-section">
              <DataTable
                data={reportsData.supplierPerformance || []}
                columns={supplierPerformanceColumns}
                loading={false}
                enableSelection={false}
                noCard={true}
                className="supplier-reports-performance-table"
                pageSize={10}
              />
            </div>
          </div>
        </>
      ) : (
        <div style={{ textAlign: 'center', padding: '40px', color: 'var(--color-text-secondary, #5B6478)' }}>
          No data available
        </div>
      )}
    </div>
  )
}

export default SupplierReports

